import {
  Box, Button, Grid, Typography, useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Content, ContentFeedSection, FeedInteractionAction } from '../../../common/models/content/Content';
import {
  getFormattedTestId,
  getHeaderImageUrl, getHeading, getHeroFormat, getImageUrl,
} from './ContentCommonFunctions';
import ContentCard from './ContentCard';
import { useDisplayContentView } from '../../../common/hooks/useDisplayContentView';
import { MORE_ROUTE } from '../../../routes/namedRoutes';
import { ContentCategory } from '../../../common/enums';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { TestIds } from '../../../assets/testIds';

interface ContentCategoryContainerProps {
  categoryId: string;
  categoryTitle?: string;
  categorySubType?: string;
  contentFeedSection: ContentFeedSection;
  storeContentList?: Content[]
}

export function ContentCategoryContainer(
  {
    categoryId,
    categoryTitle,
    categorySubType,
    contentFeedSection,
    storeContentList = [],
  }: ContentCategoryContainerProps,
) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const displayContentView = useDisplayContentView();
  const { trackFeedInteraction } = useCustomEventTracking();
  const maxNumberOfContentPerSection = 3;

  const handleViewMore = (value: string | undefined) => {
    const params = new URLSearchParams();
    const trimmedValue = value?.trim();

    if (!trimmedValue) return;
    params.set('contentCategory', trimmedValue);

    if (categoryId === ContentCategory.TOP_TRADE) {
      if (categorySubType) {
        params.set('topTradeName', categorySubType);
      }
    }
    params.set('sectionIndex', contentFeedSection.index.toString());
    params.set('sectionName', contentFeedSection.name);

    const route = `${MORE_ROUTE}?${params.toString()}`;
    trackFeedInteraction(
      'Click',
      'ContentCategoryViewMore',
      {
        action: FeedInteractionAction.Click,
        clickTime: new Date().toISOString(),
        contentFeedSectionName: contentFeedSection,
      },
    );

    navigate(route);
  };

  const showViewMoreButton = storeContentList.length >= maxNumberOfContentPerSection;

  return (
    <Box
      sx={{
        padding: `${theme.spacing(2)} ${theme.spacing(0)}`,
      }}
    >
      <Box
        data-testid={`content-feed-section-${getFormattedTestId(categoryTitle || '')}`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: theme.spacing(4),
          alignSelf: 'stretch',
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch',
          }}>
          <Typography variant='h12'
            sx={{
              [theme.breakpoints.down('sm')]: {
                fontSize: theme.spacing(2.5),
              },
              ...(showViewMoreButton && { cursor: 'pointer' }),
            }}
            {...(showViewMoreButton && { onClick: () => handleViewMore(categoryId) })}
          >{categoryTitle}
          </Typography>
          {showViewMoreButton && <Button
            data-testid={`${TestIds.contentFeed.viewMoreBtn}-${getFormattedTestId(categoryTitle || '')}-btn`}
            sx={{
              color: theme.palette.grey[800],
              padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
              gap: theme.spacing(0.5),
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              transition: 'background-image 0.15s ease',
              '&:hover': {
                backgroundColor: theme.palette.common.white,
                backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
              },
              '&:active': {
                backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
              },
              borderRadius: theme.spacing(1),
              border: `1px solid ${theme.palette.grey[300]}`,
              boxShadow: '0px 0px 0px 1px rgba(10, 13, 18, 0.18) inset, 0px -2px 0px 0px rgba(10, 13, 18, 0.05) inset, 0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
            }}
            onClick={() => handleViewMore(categoryId)}
          >
            <Typography variant='h6'>{t('button.viewMore')}</Typography>
            <ArrowForwardIcon sx={{
              width: theme.spacing(2.5),
              height: theme.spacing(2.5),
            }} />
          </Button> }
        </Box>
        <Grid
          container
          spacing={4}
          sx={{
            [theme.breakpoints.down('sm')]: {
              overflowX: 'auto',
              flexWrap: 'nowrap',
            },
          }}
        >
          {
            storeContentList
              .map((contentItem: Content) => (
                <Grid
                  id={`optimus-content-${contentItem.id}`}
                  key={contentItem.id}
                  item
                  sm={12}
                  md={4}
                  // To match the Grid spacing in the container,
                  // we need to set these paddings with !important.
                  // Sometimes it's not necessary, but when the Grid doesn't behave as expected,
                  // this helps align things properly.
                  sx={{
                    paddingTop: `${theme.spacing(4)} !important`,
                    paddingLeft: `${theme.spacing(4)} !important`,
                  }}
                >
                  <ContentCard
                    key={contentItem.id}
                    contentId={contentItem.id}
                    contentHeroListener={() => displayContentView(contentItem)}
                    contentText={contentItem.attributes?.title}
                    contentShortDescription={contentItem.attributes?.short_description}
                    categoryText={contentItem.attributes?.type}
                    banner={
                      {
                        url: getImageUrl(
                          getHeroFormat(contentItem)?.url
                          ,
                        ),
                        width: getHeroFormat(contentItem)?.width,
                        height: getHeroFormat(contentItem)?.height,
                      }}
                    authorTitle={getHeading(contentItem)}
                    publishedDate={contentItem.attributes?.source_published_date}
                    logo={getHeaderImageUrl(contentItem)}
                    videoUrl={contentItem.attributes?.video_url}
                    contentLikeStatus={contentItem.likeStatus}
                    formId={contentItem.attributes?.form_id}
                    metadata={contentItem.attributes?.metadata}
                    podcast={contentItem.attributes?.podcast}
                    contentFeedSection={contentItem.contentFeedSection}
                    contentFeedSectionPosition={contentItem.contentFeedSectionPosition}
                    locked={contentItem.attributes?.locked}
                    lowestMembershipRequired={contentItem.attributes?.lowestMembershipRequired}
                  />
                </Grid>
              ))
          }
        </Grid>
      </Box >
    </Box >
  );
}
